'use client';

import { useState, useEffect, memo, useCallback } from 'react';
import { Share2, Copy, Check, AlertCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { AutoTextarea } from '@/components/ui/auto-textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { EmailChipInput } from '@/components/ui/email-chip-input';
import { createShareUrl } from '@/lib/share-utils';
import { useShareMode } from '@/components/ShareModeProvider';
import { useAuth } from '@/contexts/AuthContext';
import { formatCurrencyForEmail, isValidEmail } from '@/lib/email-utils';
import { CalculatorInputs, PricingConfig, CalculationResults } from '@/types/calculator';
import { hasPositiveNetAnnualSavings } from '@/lib/pricing-helpers';

interface ShareButtonProps {
  inputs: CalculatorInputs;
  currency: string;
  calculatorMode: 'wizard' | 'detailed' | 'simple';
  pricing?: PricingConfig;
  results?: CalculationResults | null; // ROI calculation results
  className?: string;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  size?: 'default' | 'sm' | 'lg';
  iconOnly?: boolean;
  existingCustomName?: string;
  hasResults?: boolean;
  renderButton?: boolean;
  onCustomNameChange?: (customName: string) => void;
}

export const ShareButton = memo(function ShareButton({
  inputs,
  currency,
  calculatorMode,
  pricing,
  results,
  className,
  variant = 'outline',
  size = 'default',
  iconOnly = false,
  existingCustomName,
  hasResults = true,
  renderButton = true,
  onCustomNameChange
}: ShareButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [shareUrl, setShareUrl] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copying' | 'copied' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  
  // Email functionality state
  const [activeTab, setActiveTab] = useState<'link' | 'email'>('link');
  const [emailRecipients, setEmailRecipients] = useState<string[]>([]);
  const [ccRecipients, setCcRecipients] = useState<string[]>([]);
  const [senderNote, setSenderNote] = useState('');
  const [isEmailSending, setIsEmailSending] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState(false);
  
  // Detect if we're in share mode (on /share pages)
  const { isShareMode } = useShareMode();
  
  // Get user email for pre-populating CC field (magic link auth only)
  const { userEmail } = useAuth();

  // Pre-populate CC field with user's email when modal opens (if available)
  useEffect(() => {
    if (isOpen && userEmail && ccRecipients.length === 0) {
      setCcRecipients([userEmail]);
    }
  }, [isOpen, userEmail]); // eslint-disable-line react-hooks/exhaustive-deps

  const generateShareUrl = useCallback(async () => {
    if (isGenerating) return;
        
    if (!hasResults) {
      setError('Please calculate ROI before sharing');
      setIsGenerating(false);
      return;
    }
    
    setIsGenerating(true);
    setError(null);
    
    try {      
      const url = await createShareUrl(
        inputs,
        currency,
        calculatorMode,
        existingCustomName?.trim() || undefined,
        pricing
      );
      
      setShareUrl(url);
    } catch (err) {
      console.error('❌ Failed to generate share URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate share link');
      setShareUrl('');
    } finally {
      setIsGenerating(false);
    }
  }, [inputs, currency, calculatorMode, existingCustomName, pricing, hasResults, isGenerating]);

  // Generate share URL with specific state (not props) - fixes timing issues
  const generateShareUrlWithState = useCallback(async (
    currentInputs: CalculatorInputs,
    currentCurrency: string,
    currentCalculatorMode: 'wizard' | 'detailed' | 'simple',
    currentPricing?: PricingConfig
  ) => {
    if (isGenerating) return;
    
    console.log('🔄 generateShareUrlWithState called');
    console.log('🔍 CURRENT STATE ANALYSIS:');
    console.log('  - currentInputs.staffTurnoverRate:', currentInputs.staffTurnoverRate);
    console.log('  - currentInputs.industry:', currentInputs.industry);
    console.log('  - Full currentInputs object:', JSON.stringify(currentInputs, null, 2));
    
    setIsGenerating(true);
    setError(null);
    
    try {
      console.log('📤 SENDING CURRENT STATE TO API - staffTurnoverRate:', currentInputs.staffTurnoverRate);
      
      const url = await createShareUrl(
        currentInputs,
        currentCurrency,
        currentCalculatorMode,
        existingCustomName?.trim() || undefined,
        currentPricing
      );
      
      setShareUrl(url);
    } catch (err) {
      console.error('❌ Failed to generate share URL with current state:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate share link');
      setShareUrl('');
    } finally {
      setIsGenerating(false);
    }
  }, [existingCustomName, isGenerating]);

  // Listen for keyboard shortcut trigger
  useEffect(() => {
    const handleTriggerShare = (event: Event) => {
      const customEvent = event as CustomEvent;
      const eventData = customEvent.detail;
      
      console.log('🎬 handleTriggerShare received event with detail:', eventData);
      
      // Handle error from event data
      if (eventData?.error) {
        setError(eventData.error);
        setShareUrl('');
        setIsGenerating(false);
        setIsOpen(true);
        return;
      }

      if (!hasResults) {
        setError('Please calculate ROI before sharing');
        setShareUrl('');
        setIsGenerating(false);
        setIsOpen(true);
        return;
      }

      // Always clear shareUrl and generate fresh URL to capture current state
      setShareUrl('');
      setError(null);
      setCopyStatus('idle');
      
      // Handle custom name from event data if provided
      if (eventData?.customName && onCustomNameChange) {
        onCustomNameChange(eventData.customName);
      }
      
      setIsOpen(true);
      
      // If event contains current state, use it instead of potentially stale props
      if (eventData?.currentInputs) {
        console.log('🔄 Using current state from event - staffTurnoverRate:', eventData.currentInputs.staffTurnoverRate);
        generateShareUrlWithState(
          eventData.currentInputs,
          eventData.currentCurrency || currency,
          eventData.currentCalculatorMode || calculatorMode,
          eventData.currentPricing || pricing
        );
      } else {
        // Fallback to props if no current state in event
        console.log('⚠️ Using fallback props - staffTurnoverRate:', inputs.staffTurnoverRate);
        generateShareUrl();
      }
    };

    document.addEventListener('triggerShare', handleTriggerShare);
    return () => {
      document.removeEventListener('triggerShare', handleTriggerShare);
    };
  }, [hasResults, onCustomNameChange, generateShareUrl, generateShareUrlWithState, currency, calculatorMode, pricing, inputs.staffTurnoverRate]);

  const copyToClipboard = async () => {
    if (!shareUrl || copyStatus === 'copying') return;
    
    setCopyStatus('copying');
    
    try {
      // Try the modern clipboard API first
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback for older browsers or non-secure contexts
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 2000);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 2000);
    }
  };

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      // Always generate fresh URL to capture current state
      setError(null);
      setCopyStatus('idle');
      generateShareUrl();
    } else {
      // Reset state when closing - but don't reset custom name (parent manages it)
      setShareUrl('');
      setCopyStatus('idle');
      setError(null);
    }
  };

  const getCopyButtonContent = () => {
    switch (copyStatus) {
      case 'copying':
        return (
          <>
            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-900 rounded-full" />
            Copying...
          </>
        );
      case 'copied':
        return (
          <>
            <Check className="h-4 w-4" />
            Copied!
          </>
        );
      case 'error':
        return (
          <>
            <AlertCircle className="h-4 w-4" />
            Failed
          </>
        );
      default:
        return (
          <>
            <Copy className="h-4 w-4" />
            Copy Link
          </>
        );
    }
  };

  // Email functionality helpers

  const validateEmailList = (emails: string[]): string[] => {
    return emails.filter(email => email.trim() && isValidEmail(email.trim()));
  };

  const sendEmail = async () => {
    if (!shareUrl || !results) return;

    setIsEmailSending(true);
    setEmailError(null);

    try {
      // Validate recipients
      const validRecipients = validateEmailList(emailRecipients);
      const validCcRecipients = validateEmailList(ccRecipients);

      if (validRecipients.length === 0 && validCcRecipients.length === 0) {
        setEmailError('Please enter at least one valid email address in To or CC');
        setIsEmailSending(false);
        return;
      }
      
      // Calculate the AccessNow annual cost
      const accessNowAnnualCost = results.accessNowPricing?.totalCost || 0;
      
      // Detect scenario: Positive ROI vs Strategic Investment
      const isPositiveROI = hasPositiveNetAnnualSavings(results);
      
      // Prepare scenario-specific data
      let roiData;
      
      if (isPositiveROI) {
        // Green scenario: Traditional ROI metrics
        const totalCostSavings = results.businessEfficiencyValue; // Use business efficiency value as total savings
        const roiPercentage = accessNowAnnualCost > 0 ? 
          Math.round(((results.businessEfficiencyValue - accessNowAnnualCost) / accessNowAnnualCost) * 100) : 0;
        
        roiData = {
          isPositiveROI: true,
          totalSavings: formatCurrencyForEmail(totalCostSavings, currency),
          totalCost: formatCurrencyForEmail(accessNowAnnualCost, currency),
          roiPercentage: roiPercentage,
          currency: currency
        };
      } else {
        // Blue scenario: Strategic investment metrics
        const efficiencyGain = results.totalManualCost > 0 ? 
          Math.round((results.businessEfficiencyValue / results.totalManualCost) * 100) : 0;
        
        roiData = {
          isPositiveROI: false,
          businessEfficiencyValue: formatCurrencyForEmail(results.businessEfficiencyValue, currency),
          totalCost: formatCurrencyForEmail(accessNowAnnualCost, currency),
          efficiencyGain: efficiencyGain,
          currency: currency
        };
      }

      const emailRequest = {
        to: validRecipients,
        cc: validCcRecipients.length > 0 ? validCcRecipients : undefined,
        shareUrl: shareUrl,
        customName: existingCustomName,
        senderNote: senderNote.trim() || undefined,
        roiData: roiData
      };

      const response = await fetch('/api/email/send', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(emailRequest),
      });

      if (response.ok) {
        setEmailSuccess(true);
        // Don't auto-reset - let user manually close or send another email
      } else {
        const errorData = await response.json();
        setEmailError(errorData.error || 'Failed to send email');
      }
    } catch (error) {
      console.error('Email send error:', error);
      setEmailError('Failed to send email. Please try again.');
    } finally {
      setIsEmailSending(false);
    }
  };

  return (
    <>
      {renderButton && (
        <Button 
          variant={variant} 
          size={size} 
          className={`bg-accessnow-orange hover:bg-accessnow-orange/90 text-white border-accessnow-orange ${className}`}
          onClick={() => handleOpenChange(true)}
          aria-label={iconOnly ? "Share calculation" : undefined}
        >
          <Share2 className={`h-4 w-4 ${iconOnly ? '' : 'mr-2'}`} />
          {!iconOnly && 'Share Calculation'}
        </Button>
      )}
      
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Share Your Calculation</DialogTitle>
            <DialogClose onClose={() => handleOpenChange(false)} />
          </DialogHeader>
          
          <div className="space-y-4 p-6 pt-0">
            {/* Custom Name Field */}
            <div className="space-y-2">
              <Label htmlFor="custom-name">
                Custom Name (Optional)
              </Label>
              <Input
                id="custom-name"
                placeholder="e.g., 'Acme Corp Analysis' or 'Q4 ROI Review'"
                value={existingCustomName || ''}
                onChange={(e) => {
                  if (onCustomNameChange) {
                    onCustomNameChange(e.target.value);
                  }
                }}
                onBlur={generateShareUrl}
                maxLength={50}
              />
              <p className="text-xs text-muted-foreground">
                This name will be displayed when someone opens your shared link
              </p>
            </div>

            <Separator />

            {/* Tabbed Interface - only show tabs if NOT in share mode */}
            {!isShareMode ? (
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'link' | 'email')}>
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="link" className="flex items-center gap-2">
                    <Share2 className="h-4 w-4" />
                    Copy Link
                  </TabsTrigger>
                  <TabsTrigger value="email" className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    Send Email
                  </TabsTrigger>
                </TabsList>

                {/* Link Tab */}
                <TabsContent value="link" className="space-y-4">
                  {error ? (
                    <div className="p-3 border rounded-md bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
                      <div className="flex items-center text-red-800 dark:text-red-200">
                        <AlertCircle className="h-4 w-4 mr-2" />
                        <span className="text-sm">{error}</span>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={generateShareUrl}
                        className="mt-2"
                      >
                        Try Again
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        {isGenerating ? (
                          <div className="flex items-center flex-1 p-3 border rounded-md bg-muted/30">
                            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-900 rounded-full mr-2" />
                            <span className="text-sm">Generating link...</span>
                          </div>
                        ) : (
                          <Input
                            value={shareUrl}
                            readOnly
                            className="flex-1 text-sm"
                            placeholder="Share link will appear here"
                            onClick={(e) => shareUrl && e.currentTarget.select()}
                          />
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={copyToClipboard}
                          disabled={copyStatus === 'copying' || isGenerating || !shareUrl}
                          className={`shrink-0 ${
                            copyStatus === 'copied' 
                              ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200' 
                              : copyStatus === 'error'
                              ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
                              : ''
                          }`}
                        >
                          {getCopyButtonContent()}
                        </Button>
                      </div>
                      
                      {shareUrl && (
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>
                            Link length: ~{shareUrl.length} characters
                          </p>
                          <p>
                            Anyone with this link can view your calculation inputs and results
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </TabsContent>

                {/* Email Tab */}
                <TabsContent value="email" className="space-y-4">
                  {emailSuccess ? (
                    <div className="p-4 border rounded-md bg-green-50 dark:bg-green-950 border-green-200 dark:border-green-800 text-center space-y-3">
                      <Check className="h-8 w-8 text-green-600 dark:text-green-400 mx-auto mb-2" />
                      <p className="text-green-800 dark:text-green-200 font-medium">Email sent successfully!</p>
                      <p className="text-sm text-green-600 dark:text-green-400">
                        Recipients will receive the ROI calculation via email
                      </p>
                      <Button
                        onClick={() => {
                          setEmailSuccess(false);
                          setEmailRecipients([]);
                          setCcRecipients([]);
                          setSenderNote('');
                        }}
                        variant="outline"
                        className="border-green-300 text-green-800 hover:bg-green-100 dark:border-green-700 dark:text-green-200 dark:hover:bg-green-900/50"
                      >
                        Send Another Email
                      </Button>
                    </div>
                  ) : (
                    <>
                      {/* Email Recipients */}
                      <div className="space-y-2">
                        <Label>To (Recipients)</Label>
                        <EmailChipInput
                          emails={emailRecipients}
                          onChange={setEmailRecipients}
                          placeholder="Enter email addresses"
                        />
                        <p className="text-xs text-muted-foreground">
                          Type email addresses and press space, enter, or comma to add them
                        </p>
                      </div>

                      {/* CC Recipients */}
                      <div className="space-y-2">
                        <Label>CC (Optional)</Label>
                        <EmailChipInput
                          emails={ccRecipients}
                          onChange={setCcRecipients}
                          placeholder="Enter CC email addresses"
                        />
                      </div>

                      {/* Sender Note */}
                      <div className="space-y-2">
                        <Label htmlFor="sender-note">Personal Note (Optional)</Label>
                        <AutoTextarea
                          id="sender-note"
                          placeholder="Add a personal message to include"
                          value={senderNote}
                          onChange={(e) => setSenderNote(e.target.value)}
                          maxLength={500}
                          minRows={2}
                          maxRows={6}
                        />
                        <p className="text-xs text-muted-foreground">
                          {senderNote.length}/500 characters
                        </p>
                      </div>

                      {/* Email Error */}
                      {emailError && (
                        <div className="p-3 border rounded-md bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
                          <div className="flex items-center text-red-800 dark:text-red-200">
                            <AlertCircle className="h-4 w-4 mr-2" />
                            <span className="text-sm">{emailError}</span>
                          </div>
                        </div>
                      )}

                      {/* Send Button */}
                      <Button
                        onClick={sendEmail}
                        disabled={isEmailSending || !shareUrl || !(
                          emailRecipients.some(email => email.trim() && isValidEmail(email.trim())) ||
                          ccRecipients.some(email => email.trim() && isValidEmail(email.trim()))
                        )}
                        className="w-full bg-accessnow-orange hover:bg-accessnow-orange/90"
                      >
                        {isEmailSending ? (
                          <>
                            <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-white rounded-full mr-2" />
                            Sending Email...
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4 mr-2" />
                            Send Email
                          </>
                        )}
                      </Button>
                    </>
                  )}
                </TabsContent>
              </Tabs>
            ) : (
              /* Show only link functionality when in share mode */
              <div className="space-y-3">
                <Label>Share Link</Label>
                
                {error ? (
                  <div className="p-3 border rounded-md bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800">
                    <div className="flex items-center text-red-800 dark:text-red-200">
                      <AlertCircle className="h-4 w-4 mr-2" />
                      <span className="text-sm">{error}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={generateShareUrl}
                      className="mt-2"
                    >
                      Try Again
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      {isGenerating ? (
                        <div className="flex items-center flex-1 p-3 border rounded-md bg-muted/30">
                          <div className="animate-spin h-4 w-4 border-2 border-gray-300 border-t-gray-900 rounded-full mr-2" />
                          <span className="text-sm">Generating link...</span>
                        </div>
                      ) : (
                        <Input
                          value={shareUrl}
                          readOnly
                          className="flex-1 text-sm"
                          placeholder="Share link will appear here"
                          onClick={(e) => shareUrl && e.currentTarget.select()}
                        />
                      )}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyToClipboard}
                        disabled={copyStatus === 'copying' || isGenerating || !shareUrl}
                        className={`shrink-0 ${
                          copyStatus === 'copied' 
                            ? 'bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200' 
                            : copyStatus === 'error'
                            ? 'bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200'
                            : ''
                        }`}
                      >
                        {getCopyButtonContent()}
                      </Button>
                    </div>
                    
                    {shareUrl && (
                      <div className="text-xs text-muted-foreground space-y-1">
                        <p>
                          Link length: ~{shareUrl.length} characters
                        </p>
                        <p>
                          Anyone with this link can view your calculation inputs and results
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            <Separator />

            <div className="text-xs text-muted-foreground space-y-1">
              <p><strong>What&apos;s included:</strong></p>
              <ul className="ml-4 space-y-0.5 list-disc">
                <li>All calculator inputs and settings</li>
                <li>Currency selection ({currency})</li>
                <li>Custom pricing configurations (if modified)</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
});